
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Profile } from "@/services/profiles/types";
import { fetchAllProfiles } from "@/services/profiles/fetchAllProfiles";

export type ProfileFilter = {
  searchTerm: string;
  country?: string | null;
  university?: string | null;
  expertise?: string | null;
};

export const useCommunityProfiles = () => {
  const [filters, setFilters] = useState<ProfileFilter>({
    searchTerm: "",
    country: null,
    university: null,
    expertise: null,
  });

  const { data: profiles = [], isLoading, error, refetch } = useQuery({
    queryKey: ["profiles"],
    queryFn: fetchAllProfiles,
  });

  // Extract unique filter options for dropdowns
  const filterOptions = useMemo(() => {
    const countries = new Set<string>();
    const universities = new Set<string>();
    const expertiseAreas = new Set<string>();

    profiles.forEach((profile) => {
      if (profile.country) countries.add(profile.country);
      if (profile.university) universities.add(profile.university);
      if (profile.expertise) {
        // Handle expertise fields that might contain multiple values
        profile.expertise.split(',').forEach(exp => 
          expertiseAreas.add(exp.trim())
        );
      }
    });

    return {
      countries: Array.from(countries).sort(),
      universities: Array.from(universities).sort(),
      expertiseAreas: Array.from(expertiseAreas).sort(),
    };
  }, [profiles]);

  // Apply filters to profiles
  const filteredProfiles = useMemo(() => {
    return profiles.filter((profile) => {
      // Skip profiles without display name or username
      if (!profile.display_username && !profile.username) return false;

      // Apply text search across multiple fields
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        const displayName = profile.display_username || profile.username || "";
        const lab = profile.lab || "";
        const university = profile.university || "";
        const interests = profile.research_interests || "";
        const expertise = profile.expertise || "";
        
        const matchesSearch = 
          displayName.toLowerCase().includes(searchLower) ||
          lab.toLowerCase().includes(searchLower) ||
          university.toLowerCase().includes(searchLower) ||
          interests.toLowerCase().includes(searchLower) ||
          expertise.toLowerCase().includes(searchLower);
          
        if (!matchesSearch) return false;
      }

      // Apply dropdown filters - skip special "all-" values that represent "All Options"
      if (filters.country && filters.country !== "all-countries" && profile.country !== filters.country) return false;
      if (filters.university && filters.university !== "all-universities" && profile.university !== filters.university) return false;
      
      // For expertise, check if the profile's expertise contains the selected expertise
      if (filters.expertise && filters.expertise !== "all-expertise" && profile.expertise) {
        const profileExpertise = profile.expertise.toLowerCase().split(',').map(e => e.trim());
        if (!profileExpertise.includes(filters.expertise.toLowerCase())) return false;
      } else if (filters.expertise && filters.expertise !== "all-expertise") {
        return false; // Filter out profiles without expertise when expertise filter is applied
      }

      return true;
    });
  }, [profiles, filters]);

  return {
    profiles: filteredProfiles,
    allProfiles: profiles,
    isLoading,
    error,
    filters,
    setFilters,
    filterOptions,
    refetch,
  };
};
